//src/hooks/useSurveyCampaigns.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addSurveyCampaignParticipant,
  closeSurveyCampaign,
  createSurveyCampaign,
  deactivateSurveyCampaignParticipant,
  getLatestSurveyCampaignInviteEmailJob,
  getSurveyCampaign,
  getSurveyCampaignEmailSettings,
  listSurveyCampaignParticipantHistory,
  listSurveyCampaigns,
  listSurveyCampaignTokens,
  previewSurveyCampaignAudience,
  publishSurveyCampaign,
  reactivateSurveyCampaignParticipant,
  reopenSurveyCampaign,
  sendSurveyCampaignInviteEmails,
  sendSurveyCampaignRecipientInviteEmail,
  updateSurveyCampaignRecipientEmailStatus,
  testSurveyCampaignEmailSettings,
  updateSurveyCampaign,
  updateSurveyCampaignSchedule,
  upsertSurveyCampaignEmailSettings,
} from "@/services/surveyCampaigns";
import type {
  SurveyCampaignCreateIn,
  SurveyCampaignScheduleUpdateIn,
  SurveyCampaignUpdateIn,
} from "@/types/gro";
import type { SurveyCampaignEmailSettingsInput } from "@/services/surveyCampaigns";

export const SURVEY_CAMPAIGNS_QK = (params: { status?: string; q?: string }) =>
  ["survey_campaigns", params] as const;

export function useSurveyCampaigns(params: { status?: string; q?: string }) {
  return useQuery({
    queryKey: SURVEY_CAMPAIGNS_QK(params),
    queryFn: () => listSurveyCampaigns(params),
    staleTime: 20_000,
  });
}

export function useSurveyCampaignDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["survey_campaign_detail", id],
    queryFn: () => getSurveyCampaign(id as string),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function useSurveyCampaignTokens(id: string | undefined) {
  return useQuery({
    queryKey: ["survey_campaign_tokens", id],
    queryFn: () => listSurveyCampaignTokens(id as string),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function usePreviewSurveyCampaignAudience() {
  return useMutation({
    mutationFn: (body: SurveyCampaignCreateIn) => previewSurveyCampaignAudience(body),
  });
}

export function useCreateSurveyCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: SurveyCampaignCreateIn) => createSurveyCampaign(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
    },
  });
}

export function useUpdateSurveyCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SurveyCampaignUpdateIn }) =>
      updateSurveyCampaign(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.id] });
    },
  });
}

export function useUpdateSurveyCampaignSchedule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: SurveyCampaignScheduleUpdateIn;
    }) => updateSurveyCampaignSchedule(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.id] });
    },
  });
}

export function usePublishSurveyCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => publishSurveyCampaign(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", id] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_tokens", id] });
    },
  });
}

export function useCloseSurveyCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => closeSurveyCampaign(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", id] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_results", id] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_cuts", id] });
      qc.invalidateQueries({ queryKey: ["survey_risk_suggestions", id] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_response_summary", id] });
      qc.invalidateQueries({ queryKey: ["survey_risk_suggestions_queue"] });
    },
  });
}

export function useReopenSurveyCampaign() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reopenSurveyCampaign(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", id] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_tokens", id] });
    },
  });
}

export function useAddSurveyCampaignParticipant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      userId,
    }: {
      campaignId: string;
      userId: string;
    }) => addSurveyCampaignParticipant(campaignId, { user_id: userId }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", variables.campaignId] });
    },
  });
}

export function useDeactivateSurveyCampaignParticipant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      recipientId,
    }: {
      campaignId: string;
      recipientId: string;
    }) => deactivateSurveyCampaignParticipant(campaignId, recipientId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({
        queryKey: [
          "survey_campaign_participant_history",
          variables.campaignId,
          variables.recipientId,
        ],
      });
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", variables.campaignId] });
    },
  });
}

export function useReactivateSurveyCampaignParticipant() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      recipientId,
    }: {
      campaignId: string;
      recipientId: string;
    }) => reactivateSurveyCampaignParticipant(campaignId, recipientId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
      qc.invalidateQueries({
        queryKey: [
          "survey_campaign_participant_history",
          variables.campaignId,
          variables.recipientId,
        ],
      });
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", variables.campaignId] });
    },
  });
}

export function useSurveyCampaignParticipantHistory(
  campaignId: string | undefined,
  recipientId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["survey_campaign_participant_history", campaignId, recipientId],
    queryFn: () =>
      listSurveyCampaignParticipantHistory(campaignId as string, recipientId as string),
    enabled: !!campaignId && !!recipientId && enabled,
    staleTime: 10_000,
  });
}

export function useSurveyCampaignEmailSettings() {
  return useQuery({
    queryKey: ["survey_campaign_email_settings"],
    queryFn: () => getSurveyCampaignEmailSettings(),
    staleTime: 10_000,
  });
}

export function useUpsertSurveyCampaignEmailSettings() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: SurveyCampaignEmailSettingsInput) => upsertSurveyCampaignEmailSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_email_settings"] });
    },
  });
}

export function useTestSurveyCampaignEmailSettings() {
  return useMutation({
    mutationFn: () => testSurveyCampaignEmailSettings(),
  });
}

export function useSendSurveyCampaignInviteEmails() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) => sendSurveyCampaignInviteEmails(campaignId),
    onSuccess: (_data, campaignId) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
    },
  });
}



export function useUpdateSurveyCampaignRecipientEmailStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      recipientId,
      statusSendEmail,
    }: {
      campaignId: string;
      recipientId: string;
      statusSendEmail: "pending";
    }) => updateSurveyCampaignRecipientEmailStatus(campaignId, recipientId, statusSendEmail),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
    },
  });
}

export function useSendSurveyCampaignRecipientInviteEmail() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      recipientId,
    }: {
      campaignId: string;
      recipientId: string;
    }) => sendSurveyCampaignRecipientInviteEmail(campaignId, recipientId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_invite_job_latest", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaigns"] });
    },
  });
}

export function useLatestSurveyCampaignInviteEmailJob(
  campaignId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["survey_campaign_invite_job_latest", campaignId],
    queryFn: () => getLatestSurveyCampaignInviteEmailJob(campaignId as string),
    enabled: !!campaignId && enabled,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const status = (query.state.data as any)?.status;
      return status === "pending" || status === "processing" ? 5000 : false;
    },
  });
}
